import {
  ChartScanActivity,
  ChartScanDistanceData,
} from '@/lib/api/company/chartScan/types'
import {
  MINUTES_PER_DAY,
  parseChartIso,
  toChartIso,
} from '@/modules/imports/components/TachographChartCanvas/discPolar'

// Chart time rules, kept 1:1 with the server's ChartDayTimeline (WPF TachographChart).
// The editor holds clock times on the disc's date; the real moment is counted from the
// day start, so a clock time before the day start belongs to the next calendar day.

export type DayPiece = { activity: ChartScanActivity; start: number; end: number }

export function clockMinute(iso: string): number {
  const d = parseChartIso(iso)
  return d.getUTCHours() * 60 + d.getUTCMinutes()
}

export function dayOffset(clock: number, dayStart: number): number {
  return (((clock - dayStart) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
}

// Equal boundaries mean the whole disc is the day.
export function dayLength(dayStartIso: string, dayEndIso: string): number {
  return dayOffset(clockMinute(dayEndIso), clockMinute(dayStartIso)) || MINUTES_PER_DAY
}

// Real date-time of a minute counted from the day start.
export function offsetToIso(dayStartIso: string, offset: number): string {
  return toChartIso(new Date(parseChartIso(dayStartIso).getTime() + offset * 60_000))
}

// Activities cut at both day boundaries; only the in-day parts, ordered from the day start.
export function inDayPieces(
  activities: ChartScanActivity[],
  dayStartIso: string,
  dayEndIso: string,
): DayPiece[] {
  const dayStart = clockMinute(dayStartIso)
  const length = dayLength(dayStartIso, dayEndIso)
  const pieces: DayPiece[] = []
  const add = (activity: ChartScanActivity, start: number, end: number) => {
    const clipped = Math.min(end, length)
    if (start < clipped) pieces.push({ activity, start, end: clipped })
  }
  for (const activity of activities) {
    const from = clockMinute(activity.startTime)
    const start = dayOffset(from, dayStart)
    let duration = dayOffset(clockMinute(activity.endTime), from)
    if (duration === 0) {
      if (activities.length > 1) continue
      duration = MINUTES_PER_DAY
    }
    const end = start + duration
    add(activity, start, Math.min(end, MINUTES_PER_DAY))
    if (end > MINUTES_PER_DAY) add(activity, 0, end - MINUTES_PER_DAY)
  }
  return pieces.sort((a, b) => a.start - b.start)
}

// WPF FitStartTimeToChart: the boundary between an activity and the one before it stays
// strictly between that one's start and this one's end; the first activity follows the last.
// Returns null when nothing moves.
export function moveActivityStart(
  activities: ChartScanActivity[],
  index: number,
  minute: number,
): ChartScanActivity[] | null {
  const count = activities.length
  if (count < 2 || index < 0 || index >= count) return null
  const previousIndex = (index - 1 + count) % count
  const previous = activities[previousIndex]
  const current = activities[index]
  const from = clockMinute(previous.startTime)
  const span = dayOffset(clockMinute(current.endTime), from) || MINUTES_PER_DAY
  if (span < 2) return null
  let offset = dayOffset(Math.round(minute), from)
  if (offset < 1 || offset > span - 1) {
    const pastEnd = offset - (span - 1)
    const beforeStart = MINUTES_PER_DAY - offset + 1
    offset = offset !== 0 && pastEnd <= beforeStart ? span - 1 : 1
  }
  const clock = (from + offset) % MINUTES_PER_DAY
  if (clock === clockMinute(current.startTime)) return null
  const hhmm = `${String(Math.floor(clock / 60)).padStart(2, '0')}:${String(clock % 60).padStart(2, '0')}`
  const iso = `${current.startTime.slice(0, 11)}${hhmm}:00`
  return activities.map((a, i) => {
    if (i === index) return { ...a, startTime: iso }
    if (i === previousIndex) return { ...a, endTime: iso }
    return a
  })
}

// Canonical 800x800 chart, centre 400.
export function pointClockMinute(x: number, y: number): number {
  const angle = (Math.atan2(y - 400, x - 400) * 180) / Math.PI + 180
  return Math.round((angle * MINUTES_PER_DAY) / 360) % MINUTES_PER_DAY
}

// 1:1 port of WPF TachographChart.RefreshDistanceFromChart. vehicleOffsets are the minutes
// from the day start at which each vehicle begins (first vehicle = 0), sorted. Every
// start<->end transition is 5 km; one that straddles a vehicle change is split by time.
export function distancePerVehicle(
  data: Pick<ChartScanDistanceData, 'startPoints' | 'endPoints'>,
  vehicleOffsets: number[],
  dayStart: number,
  length: number,
): number[] {
  const toPoint = (isStart: boolean) => (p: { x: number; y: number }) => ({
    time: dayOffset(pointClockMinute(p.x, p.y), dayStart),
    isStart,
  })
  const points = [
    ...data.startPoints.map(toPoint(true)),
    ...data.endPoints.map(toPoint(false)),
  ].sort((a, b) => a.time - b.time)

  const result: number[] = []
  let index = 0
  let rest = 0
  let previous: { time: number; isStart: boolean } | null = null
  for (let vehicle = 0; vehicle < vehicleOffsets.length; vehicle++) {
    let distance = 0
    const current = vehicleOffsets[vehicle]
    const next = vehicle + 1 < vehicleOffsets.length ? vehicleOffsets[vehicle + 1] : null

    if (rest > 0 && (next == null || (index < points.length && points[index].time < next))) {
      distance = rest
      rest = 0
      previous = null
    }

    while (
      index < points.length &&
      (next == null || points[index].time <= next) &&
      points[index].time <= length
    ) {
      const point = points[index++]
      if (previous && previous.isStart !== point.isStart) distance += 5
      previous = point
    }

    if (
      previous &&
      next != null &&
      index < points.length &&
      points[index].isStart !== previous.isStart &&
      points[index].time <= length
    ) {
      if (previous.time > current) rest = 5
      const from = Math.max(previous.time, current)
      const to = Math.min(points[index].time, next)
      const part = Math.round((5 * (to - from)) / (points[index].time - previous.time))
      rest -= part
      distance += part
    }

    result.push(distance)
  }
  return result
}
